// Decodes snapshot lines (column-wise records, numbered links, meta-indexed ids) into plain objects for the store.

import type { AnyObj, BootstrapLine, FieldDef, LinkObj, MetaObj, RecordObj } from '@confdeck/core-shared';

export class SnapshotDecoder {
  private metaIds: string[] = [];
  private recordIds: string[] = [];
  private refFields = new Map<string, Set<string>>();

  /** Returns the objects a line carries; the header and end lines carry none. */
  decode(l: BootstrapLine): AnyObj[] {
    if (l.t === 'meta') {
      this.metaIds = l.objs.map((o) => o.id);
      for (const o of l.objs) {
        if (o.kind !== 'field') continue;
        const f = o as FieldDef;
        if (f.fieldType === 'taxonomy' || f.fieldType === 'user') {
          let s = this.refFields.get(f.typeId);
          if (!s) this.refFields.set(f.typeId, (s = new Set()));
          s.add(f.key);
        }
      }
      return l.objs as MetaObj[];
    }
    if (l.t === 'records') {
      const refs = this.refFields.get(l.typeId) ?? new Set<string>();
      const ids = this.metaIds;
      const ref = (v: unknown): unknown => (typeof v === 'number' ? ids[v] ?? v : Array.isArray(v) ? v.map(ref) : v);
      const out: RecordObj[] = new Array(l.rows.length);
      const cols = l.fields;
      for (let i = 0; i < l.rows.length; i++) {
        const r = l.rows[i]!;
        const data: Record<string, unknown> = {};
        for (let c = 0; c < cols.length; c++) {
          const v = r[6 + c];
          if (v === null || v === undefined) continue;
          const k = cols[c]!;
          // «*» carries keys without a field (an archived field's values).
          if (k === '*') Object.assign(data, v as Record<string, unknown>);
          else data[k] = refs.has(k) ? ref(v) : v;
        }
        this.recordIds.push(r[0]);
        out[i] = {
          id: r[0], kind: 'record', typeId: l.typeId, data,
          ownerId: r[1] == null ? null : (ref(r[1]) as string), createdBy: r[2] == null ? null : (ref(r[2]) as string),
          createdAt: new Date(r[3]).toISOString(), updatedAt: new Date(r[4]).toISOString(), rev: r[5], archivedAt: null,
        };
      }
      return out;
    }
    if (l.t === 'links') {
      const rid = (v: string | number) => (typeof v === 'number' ? this.recordIds[v]! : v);
      return l.rows.map((r): LinkObj => ({ id: r[0], kind: 'link', relationId: l.relationId, fromId: rid(r[1]), toId: rid(r[2]), data: {}, createdAt: '', rev: r[3], archivedAt: null }));
    }
    return [];
  }
}

/**
 * Splits a byte stream into NDJSON lines without holding the response in memory. A newline is searched
 * only in the new chunk and parts are joined once per line: snapshot lines are megabytes long, and
 * re-scanning the growing string on every chunk made the cold start quadratic.
 * Yields the parsed line and its raw text (the cache stores the text as is).
 */
export async function* ndjson(body: ReadableStream<Uint8Array>): AsyncGenerator<{ line: BootstrapLine; raw: string }> {
  const reader = body.pipeThrough(new TextDecoderStream() as unknown as ReadableWritablePair<string, Uint8Array>).getReader();
  let parts: string[] = [];
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    let start = 0;
    let nl: number;
    while ((nl = value.indexOf('\n', start)) >= 0) {
      parts.push(value.slice(start, nl));
      const raw = parts.length === 1 ? parts[0]! : parts.join('');
      parts = [];
      if (raw) yield { line: JSON.parse(raw) as BootstrapLine, raw };
      start = nl + 1;
    }
    if (start < value.length) parts.push(value.slice(start));
  }
  const raw = parts.join('');
  if (raw.trim()) yield { line: JSON.parse(raw) as BootstrapLine, raw };
}
